import React from 'react';
import { useTranslation } from 'react-i18next';
import TableSearch, { type TableFilter } from '../table/TableSearch';
import type { LibraryFilters } from '../../hooks/useLibraryFilters';

interface LibraryFilterBarProps {
  searchInput: string;
  filters: LibraryFilters;
  onSearchChange: (value: string) => void;
  onGenreChange: (value: string) => void;
  onMoodChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  compact?: boolean;
}

const GENRE_OPTIONS = [
  'Pop', 'Rock', 'Jazz', 'Classical', 'Electronic', 'Hip-Hop',
  'R&B', 'Country', 'Latin', 'Ambient', 'Lounge', 'Acoustic',
  'Indie', 'Blues', 'Folk', 'Funk', 'Soul', 'World',
];

const MOOD_OPTIONS = [
  'Energetic', 'Relaxing', 'Happy', 'Melancholic', 'Upbeat',
  'Chill', 'Romantic', 'Dramatic', 'Peaceful', 'Motivating',
  'Nostalgic', 'Dreamy', 'Groovy', 'Intense',
];

const STATUS_OPTIONS = ['pending', 'processing', 'completed', 'failed'];

export function LibraryFilterBar({
  searchInput,
  filters,
  onSearchChange,
  onGenreChange,
  onMoodChange,
  onStatusChange,
  compact = false,
}: LibraryFilterBarProps) {
  const { t } = useTranslation();

  const tableFilters: TableFilter[] | undefined = compact
    ? undefined
    : [
        {
          key: 'genre',
          label: t('admin.library.filterByGenre', 'Genre'),
          options: [
            { value: '', label: t('admin.library.filterByGenre', 'All Genres') },
            ...GENRE_OPTIONS.map((g) => ({ value: g, label: g })),
          ],
          value: filters.genres.length > 0 ? filters.genres[0] : '',
          onChange: onGenreChange,
          testId: 'library-filter-genre',
        },
        {
          key: 'mood',
          label: t('admin.library.filterByMood', 'Mood'),
          options: [
            { value: '', label: t('admin.library.filterByMood', 'All Moods') },
            ...MOOD_OPTIONS.map((m) => ({ value: m, label: m })),
          ],
          value: filters.moods.length > 0 ? filters.moods[0] : '',
          onChange: onMoodChange,
          testId: 'library-filter-mood',
        },
        {
          key: 'status',
          label: t('admin.library.filterByStatus', 'Status'),
          options: [
            { value: '', label: t('admin.library.filterByStatus', 'All Statuses') },
            ...STATUS_OPTIONS.map((s) => ({ value: s, label: s })),
          ],
          value: filters.statuses.length > 0 ? filters.statuses[0] : '',
          onChange: onStatusChange,
          testId: 'library-filter-status',
        },
      ];

  return (
    <TableSearch
      searchValue={searchInput}
      onSearchChange={onSearchChange}
      searchPlaceholder={t('common.search')}
      searchTestId="library-search-input"
      filters={tableFilters}
      debounceMs={300}
    />
  );
}
